(function (factory) {
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  else globalThis.PatchistryCuratedRotation = api;
})(function () {
  'use strict';
  const STORAGE_KEY = 'patchistry.curated.visit.v1';
  const VISIT_TTL = 60 * 60 * 1000;
  const roles = ['hat','signature','candyz'];
  function approved(look) {
    return look.enabled === true && ['approved_core','approved'].includes(look.approvalStatus) && roles.every(role => look[role]?.handle && look[role]?.variantId);
  }
  function sample(looks, count, random) {
    const remaining = [...looks], chosen = [];
    while (chosen.length < count && remaining.length) {
      const scores = remaining.map(look => (chosen.some(x => x.family === look.family) ? 0 : 2) + (chosen.some(x => x.hat.handle === look.hat.handle) ? 0 : 1));
      const max = Math.max(...scores), candidates = remaining.map((_,i)=>i).filter(i=>scores[i]===max);
      const picked = candidates[Math.min(candidates.length-1,Math.max(0,Math.floor(random()*candidates.length)))];
      chosen.push(remaining.splice(picked,1)[0]);
    }
    return chosen;
  }
  async function currentLook(look, fetchProduct) {
    try {
      const items = await Promise.all(roles.map(async role => {
        const component = look[role], product = await fetchProduct(component.handle);
        if (product.handle !== component.handle || String(product.id) !== String(component.productId)) throw Error('Product identity changed');
        const variant = product.variants.find(v => String(v.id) === String(component.variantId));
        if (!variant?.available || !Number.isSafeInteger(variant.price) || variant.price < 0) throw Error('Component unavailable');
        return {role,handle:component.handle,id:Number(component.variantId),price:variant.price,quantity:1,title:component.title,image:component.imageUrl};
      }));
      return {...look,items,totalCents:items.reduce((sum,item)=>sum+item.price,0)};
    } catch (_) { return null; } // Omit the whole look; never invent or exchange a component.
  }
  async function selectVisit({pool,fetchProduct,storage,now=Date.now(),random=Math.random,currency='USD'}) {
    const enabled = pool.looks.filter(approved);
    if (new Set(enabled.map(x=>x.id)).size !== enabled.length) throw Error('Duplicate approved look IDs');
    const cache = new Map();
    const fetchOnce = handle => {if(!cache.has(handle))cache.set(handle,Promise.resolve().then(()=>fetchProduct(handle)));return cache.get(handle);};
    const current = (await Promise.all(enabled.map(look=>currentLook(look,fetchOnce)))).filter(Boolean);
    let stored;
    try {stored=JSON.parse(storage?.getItem(STORAGE_KEY)||'null');}catch (_){}
    const valid = stored && Array.isArray(stored.ids) && new Set(stored.ids).size===stored.ids.length && Number.isFinite(stored.lastActivity) && now >= stored.lastActivity && now-stored.lastActivity<VISIT_TTL;
    // A pool update cannot silently add a replacement within an active visit.
    const selected = valid ? stored.ids.map(id=>current.find(look=>look.id===id)).filter(Boolean).slice(0,3) : sample(current,Math.min(3,current.length),random);
    const state = {ids:valid?stored.ids:selected.map(x=>x.id),lastActivity:now,poolVersion:pool.verifiedAtUtc,currency};
    try {storage?.setItem(STORAGE_KEY,JSON.stringify(state));}catch (_){}
    return {looks:selected,currency,newVisit:!valid,omittedSelected:valid?stored.ids.filter(id=>!selected.some(x=>x.id===id)):[],state};
  }
  function createVisitPicker(options) {
    let pending;
    return {
      select:()=>pending ||= selectVisit(options), // One selection per document: no timer-driven or mid-cart reshuffle.
      touch:()=>{try{const state=JSON.parse(options.storage?.getItem(STORAGE_KEY)||'null');if(state){state.lastActivity=Date.now();options.storage.setItem(STORAGE_KEY,JSON.stringify(state));}}catch(_){}},
      // Use the existing guarded cart controller for writes; this module never writes a cart.
      async addLook(look,controller){this.touch();const result=await controller.add(look.items.map(({handle,id,price})=>({handle,id,price})));this.touch();return result;}
    };
  }
  return {approved,sample,currentLook,selectVisit,createVisitPicker,STORAGE_KEY,VISIT_TTL};
});
